import { z } from 'zod';
import { ALL_PERMISSION_KEYS, DATA_SCOPES } from './permissions';

/**
 * Validation schemas shared by the web app (inline validation) and the API
 * (request validation), so both always enforce and report the same rules.
 */

/**
 * Cross-field rules run even while other fields are still invalid, so a
 * dependent error (e.g. "Passwords do not match") shows inline immediately
 * instead of only after every other field is fixed.
 */
export const always = { when: () => true } as const;

const trimmed = (label: string, max: number) =>
  z.string().trim().min(1, `${label} is required`).max(max, `${label} must be at most ${max} characters`);

export const usernameSchema = z
  .string()
  .trim()
  .min(3, 'Username must be at least 3 characters')
  .max(40, 'Username must be at most 40 characters')
  .regex(/^[a-zA-Z0-9._@-]+$/, 'Use letters, numbers, dot, dash, underscore or @ only')
  .transform((v) => v.toLowerCase());

export const PASSWORD_RULES = [
  { test: (v: string) => v.length >= 10, label: 'At least 10 characters' },
  { test: (v: string) => /[A-Z]/.test(v), label: 'An uppercase letter' },
  { test: (v: string) => /[a-z]/.test(v), label: 'A lowercase letter' },
  { test: (v: string) => /\d/.test(v), label: 'A number' },
  { test: (v: string) => /[^A-Za-z0-9]/.test(v), label: 'A symbol' },
] as const;

export const passwordSchema = z
  .string()
  .max(128, 'Password must be at most 128 characters')
  .refine((v) => PASSWORD_RULES.every((r) => r.test(v)), 'Password does not meet the requirements');

export const emailSchema = z.string().trim().toLowerCase().max(200).email('Enter a valid email address');
export const optionalEmail = z.union([z.literal(''), emailSchema]).optional();

/** UAE mobile / landline in E.164 or local form, e.g. +971 50 123 4567 or 050 123 4567. */
export const uaePhoneSchema = z
  .string()
  .trim()
  .regex(/^(\+971|00971|0)?[1-9]\d{7,8}$/, 'Enter a valid UAE phone number, e.g. +971501234567')
  .or(z.literal(''));

/** UAE Tax Registration Number: 15 digits. */
export const trnSchema = z
  .string()
  .trim()
  .regex(/^\d{15}$/, 'TRN must be exactly 15 digits')
  .or(z.literal(''));

// ---------- Auth ----------

export const loginSchema = z.object({
  username: z.string().trim().min(1, 'Username is required').max(200),
  password: z.string().min(1, 'Password is required').max(128),
  /** Six-digit code from the authenticator app, when two-step sign-in is on for the account. */
  code: z.string().trim().max(10).optional(),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: passwordSchema,
    confirmPassword: z.string().min(1, 'Please confirm the new password'),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
    ...always,
  })
  .refine((d) => d.newPassword !== d.currentPassword, {
    path: ['newPassword'],
    message: 'New password must be different from the current one',
    ...always,
  });
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

// ---------- Common ----------

export const listQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  search: z.string().trim().max(100).optional(),
  sort: z.string().max(50).optional(),
});
export type ListQuery = z.infer<typeof listQuerySchema>;

export const idSchema = z.string().uuid();

// ---------- Users ----------

const userBase = {
  username: usernameSchema,
  fullName: trimmed('Full name', 120),
  email: optionalEmail,
  phone: uaePhoneSchema.optional(),
  isActive: z.boolean(),
  branchId: z.string().uuid('Select a branch'),
  departmentId: z.string().uuid().nullable().optional(),
  /** Branches this user can see data for (in addition to their home branch). */
  branchIds: z.array(z.string().uuid()).default([]),
  roleIds: z.array(z.string().uuid()).min(1, 'Assign at least one role'),
};

export const createUserSchema = z
  .object({
    ...userBase,
    password: passwordSchema,
    confirmPassword: z.string().min(1, 'Please confirm the password'),
    mustChangePassword: z.boolean().default(true),
  })
  .refine((d) => d.password === d.confirmPassword, { path: ['confirmPassword'], message: 'Passwords do not match', ...always });
export type CreateUserInput = z.infer<typeof createUserSchema>;

export const updateUserSchema = z.object({ ...userBase, version: z.number().int() });
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

export const resetPasswordSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string().min(1, 'Please confirm the password'),
  })
  .refine((d) => d.password === d.confirmPassword, { path: ['confirmPassword'], message: 'Passwords do not match', ...always });
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

// ---------- Roles ----------

export const rolePermissionSchema = z.object({
  key: z.string().refine((k) => ALL_PERMISSION_KEYS.has(k), 'Unknown permission'),
  scope: z.enum(DATA_SCOPES).default('ALL'),
});

const roleBase = {
  code: z
    .string()
    .trim()
    .min(2, 'Code must be at least 2 characters')
    .max(30, 'Code must be at most 30 characters')
    .regex(/^[A-Z0-9_]+$/, 'Use uppercase letters, numbers and underscore only'),
  name: trimmed('Role name', 80),
  description: z.string().trim().max(300, 'Description must be at most 300 characters').optional().default(''),
  isActive: z.boolean(),
  permissions: z.array(rolePermissionSchema),
};

export const createRoleSchema = z.object(roleBase);
export type CreateRoleInput = z.infer<typeof createRoleSchema>;
export const updateRoleSchema = z.object({ ...roleBase, version: z.number().int() });
export type UpdateRoleInput = z.infer<typeof updateRoleSchema>;

// ---------- Organization ----------

export const branchSchema = z.object({
  code: z.string().trim().min(1, 'Code is required').max(10).regex(/^[A-Z0-9-]+$/, 'Use uppercase letters, numbers and dash'),
  name: trimmed('Branch name', 80),
  isActive: z.boolean(),
});
export type BranchInput = z.infer<typeof branchSchema>;

export const departmentSchema = z.object({
  code: z.string().trim().min(1, 'Code is required').max(10).regex(/^[A-Z0-9-]+$/, 'Use uppercase letters, numbers and dash'),
  name: trimmed('Department name', 80),
  isActive: z.boolean(),
});
export type DepartmentInput = z.infer<typeof departmentSchema>;

export const companySchema = z.object({
  legalName: trimmed('Legal name', 160),
  tradeName: z.string().trim().max(160).optional().default(''),
  trn: trnSchema,
  email: optionalEmail,
  phone: uaePhoneSchema.optional(),
  address: z.string().trim().max(400).optional().default(''),
  baseCurrency: z.literal('AED'),
  vatRate: z.coerce.number().min(0, 'VAT cannot be negative').max(100, 'VAT cannot exceed 100%'),
  fiscalYearStartMonth: z.coerce.number().int().min(1).max(12),
  legalNameAr: z.string().trim().max(200).optional().default(''),
  mobile: z.string().trim().max(40).optional().default(''),
  website: z.string().trim().max(120).optional().default(''),
  logo: z.string().max(400_000, 'Logo too large (max ~300 KB)').refine((v) => v === '' || /^data:image\/(png|jpeg|svg\+xml|webp);base64,/.test(v), 'Logo must be a PNG, JPEG, SVG or WebP image').optional().default(''),
  invoiceTerms: z.string().trim().max(3000).optional().default(''),
  printFooter: z.string().trim().max(500).optional().default(''),
  letterheadTopMm: z.coerce.number().int().min(0).max(120).default(45),
  letterheadBottomMm: z.coerce.number().int().min(0).max(80).default(25),
  version: z.number().int(),
});
export type CompanyInput = z.infer<typeof companySchema>;
